import { Controller } from 'stimulus'
import { onTransitionEnd } from '../assets/utils'
import loader from '../assets/loader'
import { fetchText } from '../assets/request'

const MODAL_TEMPLATE = `
    <div x-data="{show: false, content: '', size: null, panelClass: ''}"
        x-on:keyup.window="if (show && ($event.key === 'Escape')) { show = false }"
        x-on:modal-show="show = true"
        x-on:modal-hide="show = false"
        x-on:modal-set="
            await $nextTick()
            content = $event.detail.content || content;
            size = $event.detail.size || size;
            show = $event.detail.show || show;
        "
        x-init="
            $watch('size', (size) => {
                switch(size) {
                    case 'tall':
                        panelClass = 'h-full md:mx-auto md:w-8/12';
                        break;
                    case 'wide':
                        panelClass = 'max-h-full md:h-auto';
                        break;
                    case 'full':
                        panelClass = 'h-full';
                        break;
                    default:
                        panelClass = 'max-h-full md:h-auto md:mx-auto max-w-11/12 md:max-w-6/12';
                }
            });
            $watch('show', s => (s === false) && $dispatch('modal-closing'));
            size = 'default';
            $dispatch('modal-initialized')
        ">
        <template x-if="show">
            <div class="fixed z-top inset-0 bg-black bg-opacity-30"
                x-on:click.stop="show = false"
                x-show="show"
                x-transition:enter="transition-opacity ease-in duration-150"
                x-transition:enter-start="opacity-0 "
                x-transition:enter-end="opacity-100"
                x-transition:leave="transition-opacity ease-out duration-300"
                x-transition:leave-start="opacity-100"
                x-transition:leave-end="opacity-0">
                <div class="layout-container absolute inset-0 p-1 md:px-4 md:py-8"
                    x-show="show"
                    x-transition:enter="transform transition-transform ease-in duration-150"
                    x-transition:enter-start="translate-y-8"
                    x-transition:enter-end="translate-y-0"
                    x-transition:leave="transform transition-transform ease-in duration-300"
                    x-transition:leave-start="translate-y-0"
                    x-transition:leave-end="translate-y-4">
                    <div class="w-full flex">
                        <div class="bg-base-100 p-8 pt-12 border shadow-xl relative min-h-[90px] overflow-y-auto m-auto rounded rounded-xl drop-shadow-2xl"
                            x-bind:class="panelClass"
                            x-on:click.stop="">
                            <button class="absolute p-4 top-0 right-0" type="button"
                                x-on:click.stop="show = false">
                                <span class="fal fa-times fa-2x"></span>
                            </button>
                            <div x-init="$nextTick(() => $el.innerHTML = content); $watch('content', () => $el.innerHTML = content)" class="h-full">
                                <!-- CONTENT -->
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
`;

export default class extends Controller {

  static _modal = null;
  static _modal_ready = null;

  initialize() {
    this.onClick = this.onClick.bind(this);
    this.onClickClose = this.onClickClose.bind(this);
  }

  connect() {
    this.element.addEventListener('click', this.onClick);
  }

  get modal() {
    if (this.constructor._modal === null) {
      // Singleton initialisieren
      const template = document.createElement('template');
      template.innerHTML = MODAL_TEMPLATE;
      this.constructor._modal = template.content.children[0];
      this.constructor._modal_ready = new Promise(resolve => {
        this.constructor._modal.addEventListener('modal-initialized', resolve);
        this.constructor._modal.addEventListener('modal-closing', this.close.bind(this, void 0));
      })
        // kurzes delay einbauen, damit AlpineJs fertig werden kann
        .then(() => new Promise(resolve => setTimeout(resolve, 10)));
      document.getElementById('legacy-content').append(this.constructor._modal);
    }
    return this.constructor._modal;
  }

  get modal_ready() {
    if (this.constructor._modal_ready === null) {
      // modal initialisieren, um Promise zu erzeugen
      const modal = this.modal;
    }
    return this.constructor._modal_ready;
  }

  async onClick(event) {
    event.preventDefault();
    await this.modal_ready;
    this.content = await this.loadContent();
    this.open();
  }

  set content(content) {
    this.modal.dispatchEvent(new CustomEvent('modal-set', {detail: {content}}));
    setTimeout(() => this.initContent(), 1000);
  }

  set size(size) {
    this.modal.dispatchEvent(new CustomEvent('modal-set', {detail: {size}}));
  }

  set show(show) {
    this.modal.dispatchEvent(new CustomEvent('modal-set', {detail: {show}}));
  }

  async open() {
    await this.modal_ready;
    this.size = this.data.get('size');
    this.show = true;
    document.body.classList.add('overflow-hidden');
    return onTransitionEnd(this.modal)
      .then(() => {
        /** EVENT DISPATCHEN: Modal wurde geöffnet */
        const event = new CustomEvent('modal:opened');
        this.modal.querySelectorAll(`[data-controller], [x-data]`)
          .forEach(element => element.dispatchEvent(event));

      });
  }

  async onClickClose(event) {
    event.stopPropagation();
    return this.close((event.currentTarget || event.target).dataset.modalValue);
  }

  async close(value) {
    this.modal.dispatchEvent(new CustomEvent('modal-hide'));
    document.body.classList.remove('overflow-hidden');
    if ((value !== void 0) && this.element.dataset.modalModel) {
      let model = document.getElementById(this.element.dataset.modalModel);
      if (model) {
        model.value = value;
        model.dispatchEvent(new Event('change'));
        if (model.form) {
          model.form.dispatchEvent(new Event('change'));
        }
      }
    }

    return onTransitionEnd(this.modal)
      .then(() => {
        /** EVENT DISPATCHEN: Modal wurde geschlossen */
        const event = new CustomEvent('modal:closed');
        this.modal.querySelectorAll(`[data-controller], [x-data]`)
          .forEach(element => element.dispatchEvent(event));
      });
  }

  async loadContent() {
    if (this.data.get('url')) {
      loader.show();
      let content = await fetchText(this.data.get('url'));
      loader.hide();
      return content;
    } else if (this.data.get('id')) {
      return document.getElementById(this.data.get('id'))?.innerHTML ?? '';
    }
    return '';
  }

  initContent() {
    this.modal.querySelectorAll(`[data-modal-close]`)
      .forEach(element => element.addEventListener('click', this.onClickClose));
    // Formular ggf. erweitern, damit es per Ajax abgeschickt werden kann.
    const form = this.modal.querySelector("form");
    if (form && !form.getAttribute("data-controller") && !form.getAttribute('target')) {
      form.setAttribute("data-controller", "ajax-form");
      form.addEventListener("ajax-form:submit", () => loader.show());
      form.addEventListener("ajax-form:success", (event) => {
        if (typeof event.detail === 'object') {
          if (event.detail.redirect) {
            window.location.assign(event.detail.redirect);
            return;
          }
        }
        this.content = event.detail;
        loader.hide();
      });
      form.addEventListener("ajax-form:error", (event) => {
        if (event.detail.responseText) {
          this.content = event.detail.responseText;
        } else {
          this.close();
        }
        loader.hide();
      });
    }
  }
}
